/**
 * Pure status derivation for one `fleet.json` organism entry — shared by
 * the fleet screen's cards and the fleet-bar organism switcher's per-pill
 * status dot (extracted from `FleetScreen.ts` in the revamp pass so both
 * call sites can never disagree on what "recent"/"stale"/"none" means).
 *
 * Deliberately conservative: this only ever claims a *heartbeat*-derived
 * status ("started" + heartbeat recency), never a session/wake-derived
 * "awake"/"asleep" claim — only the organism screen's own event-derived
 * vitals hero (`src/panels/vitals.ts`) has the real session/wake evidence
 * to say that honestly.
 */

import type { FleetOrganismEntry } from "../types/incremental";

const RECENT_HEARTBEAT_MS = 5 * 60_000;

export type HeartbeatFreshness = "recent" | "stale" | "none";

/** OQA: what the pill says. `stopped` = the unit is not running (fleet.json
 * `unit_state` is a systemd word other than "active"); `pending` = stopped
 * with a fresh episode published as an event-less stub (`episode_status`). */
export type FleetStatusKind =
  | "active"
  | "stalled"
  | "stopped"
  | "failed"
  | "restarting"
  | "pending"
  | "starting"
  | "not_started";

export const FLEET_STATUS_WORDS: Readonly<Record<FleetStatusKind, string>> = {
  active: "Active",
  stalled: "Stalled",
  stopped: "Stopped",
  failed: "Failed · stopped",
  restarting: "Restarting",
  pending: "Stopped · fresh episode",
  starting: "Starting",
  not_started: "Not started",
};

export interface FleetStatus {
  readonly kind: FleetStatusKind;
  /** `FLEET_STATUS_WORDS[kind]` — the pill text. */
  readonly word: string;
  readonly freshness: HeartbeatFreshness;
  readonly dotClass: string;
  readonly tooltip: string;
  /** The parsed `last_heartbeat_time`, or `null` if absent/unparsable —
   * exposed so a caller's relative-time cell reuses this instead of a
   * second `Date.parse` of the same string. */
  readonly heartbeatMs: number | null;
}

function heartbeatFreshness(heartbeatMs: number | null, now: number): HeartbeatFreshness {
  if (heartbeatMs === null) return "none";
  return now - heartbeatMs <= RECENT_HEARTBEAT_MS ? "recent" : "stale";
}

/** Three real, distinct states, each with its own honest copy:
 * `started:true` with no `last_heartbeat_time` at all (a real, reachable
 * transient — the runtime unit can be active before its first publish) is
 * "no heartbeat yet", never conflated with "stale" (a heartbeat that *did*
 * arrive, just more than 5 minutes ago). */
export function fleetStatus(organism: FleetOrganismEntry, now: number): FleetStatus {
  const parsed = organism.last_heartbeat_time ? Date.parse(organism.last_heartbeat_time) : NaN;
  const heartbeatMs = Number.isFinite(parsed) ? parsed : null;
  const pending = organism.episode_status === "pending_first_wake";
  const make = (
    kind: FleetStatusKind,
    freshness: HeartbeatFreshness,
    dotClass: string,
    tooltip: string,
  ): FleetStatus => ({
    kind,
    word: FLEET_STATUS_WORDS[kind],
    freshness,
    dotClass,
    tooltip,
    heartbeatMs,
  });

  if (!organism.started) {
    const raw = (organism.unit_state ?? "").trim().toLowerCase();
    // "unknown"/empty is not a state (systemctl could not answer): no claim.
    const unitState = raw === "" || raw === "unknown" ? null : raw;
    if (unitState === "activating" || unitState === "reloading") {
      return make(
        "restarting",
        "none",
        "obs-alive-stalled",
        `The runtime unit is ${unitState} (systemd) — starting, or restarting after an ` +
          "exit (a crash loop reads like this too); figures are its last published state.",
      );
    }
    if (unitState === "failed") {
      return make(
        "failed",
        "none",
        "obs-alive-stalled",
        "The runtime unit FAILED (systemd: failed) and is not running; figures are its " +
          "last published state.",
      );
    }
    if (unitState !== null && unitState !== "active") {
      // OQA: the publisher reports the unit's real state — say "stopped", never
      // "stalled" (stalled means running but silent) or "never run".
      if (pending) {
        return make(
          "pending",
          "none",
          "obs-alive-unknown",
          `The runtime unit is stopped (systemd: ${unitState}). A fresh episode is ready — a flat ` +
            "book at its recorded opening capital — and starts at the organism's first wake.",
        );
      }
      return make(
        "stopped",
        "none",
        "obs-alive-unknown",
        `The runtime unit is stopped (systemd: ${unitState}); everything shown is its last ` +
          "published state, not live.",
      );
    }
    return make(
      "not_started",
      "none",
      "obs-alive-unknown",
      "This organism's runtime loop has never run a cycle.",
    );
  }
  if (pending) {
    return make(
      "starting",
      "none",
      "obs-alive-unknown",
      "Started on a fresh episode; waiting for its first wake to be published.",
    );
  }
  const freshness = heartbeatFreshness(heartbeatMs, now);
  if (freshness === "recent") {
    return make(
      "active",
      freshness,
      "obs-alive-awake",
      "Started, with a heartbeat in the last 5 minutes.",
    );
  }
  if (freshness === "stale") {
    return make(
      "stalled",
      freshness,
      "obs-alive-stalled",
      "Started, but its last heartbeat is more than 5 minutes old.",
    );
  }
  return make(
    "starting",
    freshness,
    "obs-alive-unknown",
    "Started, but no heartbeat has been received yet.",
  );
}
